const KEY = 'gn/cache/v1';
const DEFAULT_TTL_MS = 30 * 60 * 1000;

interface Entry<T = unknown> {
  data: T;
  ts: number;
}

type Store = Record<string, Entry>;

function readStore(): Store {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Store) : {};
  } catch {
    return {};
  }
}

function writeStore(store: Store): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(store));
  } catch {
    /* quota or disabled storage — silent */
  }
}

function isFresh(entry: Entry, ttlMs: number): boolean {
  return ttlMs === Infinity || Date.now() - entry.ts < ttlMs;
}

export function getCached<T>(key: string, ttlMs: number = DEFAULT_TTL_MS): T | null {
  const entry = readStore()[key];
  if (!entry || !isFresh(entry, ttlMs)) return null;

  return entry.data as T;
}

export function setCached<T>(key: string, data: T): void {
  const store = readStore();
  store[key] = { data, ts: Date.now() };
  writeStore(store);
}

export function invalidate(key: string): void {
  const store = readStore();
  if (!(key in store)) return;
  delete store[key];
  writeStore(store);
}

export function clearAll(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* silent */
  }
}

export async function cachedFetch<T>(
  key: string,
  fetcher: () => Promise<T>,
  ttlMs: number = DEFAULT_TTL_MS,
): Promise<T> {
  const entry = readStore()[key];

  if (entry && isFresh(entry, ttlMs)) {
    return entry.data as T;
  }

  if (entry) {
    void fetcher()
      .then((fresh) => setCached(key, fresh))
      .catch(() => undefined);

    return entry.data as T;
  }

  const fresh = await fetcher();
  setCached(key, fresh);

  return fresh;
}
